import { useCallback, useEffect, useState } from "react";
import type { ReflectionMatrix } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { Row } from "../../inspector/rows";

/**
 * Per-object reflection matrix (v30). A group×group grid where cell [dest][src]
 * scales how strongly metal group `dest` reflects metal group `src` — so
 * ring↔ring and metal-part↔metal-part reflections are independently tunable.
 *
 * All `client.ring.*` calls cross the postMessage RPC bridge and are async
 * (their contract signatures are sync, but the proxy returns Promises).
 */
export function ReflectionMatrixControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [m, setM] = useState<ReflectionMatrix | null>(null);

  const refresh = useCallback(() => {
    Promise.resolve(r.getReflectionMatrix()).then(setM).catch(() => setM(null));
  }, [r]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!m) {
    return <div className="px-3 py-2 text-[12px] text-ed-text-3">Loading…</div>;
  }

  const { groups, weights, enabled } = m;
  const setCell = (d: number, s: number, v: number) =>
    Promise.resolve(r.setReflectionMatrixCell(d, s, v)).then(setM);
  const toggle = (on: boolean) => Promise.resolve(r.setReflectionMatrixEnabled(on)).then(setM);
  const reset = () => Promise.resolve(r.resetReflectionMatrix()).then(setM);

  return (
    <div className="flex flex-col gap-2 px-3 py-1.5">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-[12px] text-ed-text">
          <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
          Enable matrix
        </label>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="text-[11px] text-ed-text-3 underline hover:text-ed-text">Refresh</button>
          <button onClick={reset} className="text-[11px] text-ed-text-3 underline hover:text-ed-text">Reset</button>
        </div>
      </div>

      {groups.length < 2 ? (
        <div className="text-[11px] leading-relaxed text-ed-text-3">
          Needs at least 2 metal groups in the model (e.g. a two-ring set, or a band with separate
          prong/shank metals). Load a model with multiple metal parts, then Refresh.
        </div>
      ) : (
        <>
          <div className="text-[11px] text-ed-text-3">
            Cell = how strongly the <b>row</b> metal reflects the <b>column</b> metal (1 = unchanged).
          </div>
          <div className="overflow-auto">
            <table className="border-collapse text-[11px]">
              <thead>
                <tr>
                  <th className="p-1 text-left font-normal text-ed-text-3">refl ↓ \ src →</th>
                  {groups.map((g) => (
                    <th key={g.id} className="max-w-[52px] truncate p-1 font-normal text-ed-text-3" title={g.label}>
                      {g.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((dg) => (
                  <tr key={dg.id}>
                    <td className="max-w-[64px] truncate p-1 text-ed-text-3" title={dg.label}>{dg.label}</td>
                    {groups.map((sg) => (
                      <td key={sg.id} className="p-0.5">
                        <input
                          type="number"
                          min={0}
                          max={2}
                          step={0.05}
                          value={weights[dg.id]?.[sg.id] ?? 1}
                          onChange={(e) => setCell(dg.id, sg.id, parseFloat(e.target.value) || 0)}
                          className="w-12 rounded border border-ed-border bg-ed-row px-1 py-0.5 text-ed-text"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Section-row wrapper (stacked, full-width custom UI). */
export function ReflectionMatrixRow() {
  return (
    <Row stacked>
      <ReflectionMatrixControl />
    </Row>
  );
}
